/**
 * Audit & SIEM API module - SIEM connector configs, audit chain
 * (tamper-proof append-only log) and audit retention policies.
 *
 * Real per-domain split: contains the actual implementations of the audit/SIEM
 * domain API objects, extracted verbatim from the monolithic
 * frontend/src/services/api.ts as part of the ADR-0003 follow-up.
 *
 * Domain objects:
 *   - siemApi             (SIEM forwarder configs - CRUD/test/force-export/stats)
 *   - auditChainApi       (append-only audit chain - verify/list/append)
 *   - auditRetentionApi   (audit retention policies - CRUD/apply/stats)
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

// -- siemApi --
export const siemApi = {
  createConfig: async (data: any): Promise<any> => _unwrap(await fetchClient.post<any>('/api/v1/audit/siem/configs', data)),
  listConfigs: async (): Promise<any> => _unwrap(await fetchClient.get<any>('/api/v1/audit/siem/configs')),
  getConfig: async (id: string): Promise<any> => _unwrap(await fetchClient.get<any>(`/api/v1/audit/siem/configs/${id}`)),
  updateConfig: async (id: string, data: any): Promise<any> => _unwrap(await fetchClient.put<any>(`/api/v1/audit/siem/configs/${id}`, data)),
  deleteConfig: async (id: string): Promise<any> => _unwrap(await fetchClient.delete<any>(`/api/v1/audit/siem/configs/${id}`)),
  testConnection: async (id: string): Promise<any> => _unwrap(await fetchClient.post<any>(`/api/v1/audit/siem/test/${id}`)),
  forceExport: async (id: string): Promise<any> => _unwrap(await fetchClient.post<any>(`/api/v1/audit/siem/export/${id}`)),
  getStats: async (): Promise<any> => _unwrap(await fetchClient.get<any>('/api/v1/audit/siem/stats')),
};

// -- auditChainApi --
export const auditChainApi = {
  verify: () => fetchClient.get('/api/v1/audit/chain/verify').then(_thenUnwrap),
  getEntries: (params?: { offset?: number; limit?: number }) => fetchClient.get('/api/v1/audit/chain/entries', params).then(_thenUnwrap),
  getEntry: (sequence: number) => fetchClient.get(`/api/v1/audit/chain/entry/${sequence}`).then(_thenUnwrap),
  append: (data: any) => fetchClient.post('/api/v1/audit/chain/append', data).then(_thenUnwrap),
};

// -- auditRetentionApi --
export const auditRetentionApi = {
  createPolicy: (data: any) => fetchClient.post('/api/v1/audit/retention/policies', data).then(_thenUnwrap),
  listPolicies: () => fetchClient.get('/api/v1/audit/retention/policies').then(_thenUnwrap),
  updatePolicy: (id: string, data: any) => fetchClient.put(`/api/v1/audit/retention/policies/${id}`, data).then(_thenUnwrap),
  deletePolicy: (id: string) => fetchClient.delete(`/api/v1/audit/retention/policies/${id}`).then(_thenUnwrap),
  apply: () => fetchClient.post('/api/v1/audit/retention/apply').then(_thenUnwrap),
  getStats: () => fetchClient.get('/api/v1/audit/retention/stats').then(_thenUnwrap),
};
